import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { useCreditPolicies } from '../../../hooks/useCredit';
import type { CreditProductType, CreditPolicy } from '../../../types/credit';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value / 100);
};

const formatPercent = (value: number) => {
  return `${(value * 100).toFixed(2)}%`;
};

const productTypes: { type: CreditProductType; name: string; description: string; icon: string }[] = [
  {
    type: 'PERSONAL_LOAN',
    name: 'Empréstimo Pessoal',
    description: 'Crédito sem garantia para pessoas físicas',
    icon: '👤',
  },
  {
    type: 'PAYROLL_LOAN',
    name: 'Crédito Consignado',
    description: 'Desconto em folha de pagamento',
    icon: '💼',
  },
  {
    type: 'RECEIVABLES_ADVANCE',
    name: 'Antecipação de Recebíveis',
    description: 'Antecipação de vendas a prazo',
    icon: '📊',
  },
  {
    type: 'WORKING_CAPITAL',
    name: 'Capital de Giro',
    description: 'Crédito para empresas (PJ)',
    icon: '🏢',
  },
];

export function CreditProducts() {
  const [selectedProduct, setSelectedProduct] = useState<CreditProductType | null>(null);
  const { data: policies } = useCreditPolicies();

  const getPolicyForProduct = (productType: CreditProductType): CreditPolicy | undefined => {
    return policies?.find((p) => p.productType === productType && p.isActive);
  };

  const getProductStatus = (productType: CreditProductType): 'active' | 'inactive' | 'no-policy' => {
    const policy = getPolicyForProduct(productType);
    if (!policy) return 'no-policy';
    return policy.isActive ? 'active' : 'inactive';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Produtos de Crédito</h1>
        <Button onClick={() => (window.location.href = '/admin/credit/policies')}>
          Gerenciar Políticas
        </Button>
      </div>

      {/* Grid de Produtos */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {productTypes.map((product) => {
          const policy = getPolicyForProduct(product.type);
          const status = getProductStatus(product.type);

          return (
            <Card
              key={product.type}
              className={`cursor-pointer transition-all hover:shadow-lg ${
                selectedProduct === product.type ? 'ring-2 ring-blue-500' : ''
              }`}
              onClick={() => setSelectedProduct(product.type)}
            >
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{product.icon}</span>
                    <div>
                      <CardTitle>{product.name}</CardTitle>
                      <p className="text-sm text-gray-500">{product.description}</p>
                    </div>
                  </div>
                  <Badge
                    variant={
                      status === 'active'
                        ? 'success'
                        : status === 'inactive'
                        ? 'default'
                        : 'error'
                    }
                  >
                    {status === 'active'
                      ? 'Ativo'
                      : status === 'inactive'
                      ? 'Inativo'
                      : 'Sem Política'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {policy ? (
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Valor Mínimo</p>
                      <p className="font-medium">{formatCurrency(policy.minAmount)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Valor Máximo</p>
                      <p className="font-medium">{formatCurrency(policy.maxAmount)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Parcelas</p>
                      <p className="font-medium">
                        {policy.minInstallments} a {policy.maxInstallments}x
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Taxa Mensal</p>
                      <p className="font-medium">{formatPercent(policy.monthlyInterestRate)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Score Mínimo</p>
                      <p className="font-medium">{policy.minScore}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Auto-Aprovação</p>
                      <p className="font-medium">
                        {policy.autoApprovalThreshold
                          ? `Score ≥ ${policy.autoApprovalThreshold}`
                          : 'Desabilitado'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-gray-500 mb-2">
                      Nenhuma política configurada para este produto.
                    </p>
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.location.href = '/admin/credit/policies';
                      }}
                    >
                      Criar Política
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Detalhes do Produto Selecionado */}
      {selectedProduct && (
        <Card>
          <CardHeader>
            <CardTitle>
              Configurações de{' '}
              {productTypes.find((p) => p.type === selectedProduct)?.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const policy = getPolicyForProduct(selectedProduct);
              if (!policy) {
                return (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">
                      Configure uma política de crédito para habilitar este produto.
                    </p>
                    <Button
                      onClick={() =>
                        (window.location.href = '/admin/credit/policies')
                      }
                    >
                      Ir para Políticas
                    </Button>
                  </div>
                );
              }

              return (
                <div className="space-y-6">
                  {/* Limites */}
                  <div>
                    <h3 className="font-medium mb-3">Limites de Valor</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Mínimo</p>
                        <p className="font-medium">{formatCurrency(policy.minAmount)}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Máximo</p>
                        <p className="font-medium">{formatCurrency(policy.maxAmount)}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Parcelas Mín.</p>
                        <p className="font-medium">{policy.minInstallments}x</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Parcelas Máx.</p>
                        <p className="font-medium">{policy.maxInstallments}x</p>
                      </div>
                    </div>
                  </div>

                  {/* Taxas */}
                  <div>
                    <h3 className="font-medium mb-3">Taxas e Encargos</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Taxa Mensal</p>
                        <p className="font-medium">{formatPercent(policy.monthlyInterestRate)}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">IOF Diário</p>
                        <p className="font-medium">{formatPercent(policy.iofDailyRate || 0)}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">IOF Adicional</p>
                        <p className="font-medium">{formatPercent(policy.iofAdditionalRate || 0)}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Taxa Originação</p>
                        <p className="font-medium">{formatPercent(policy.originationFeeValue || 0)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Critérios de Aprovação */}
                  <div>
                    <h3 className="font-medium mb-3">Critérios de Aprovação</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Score Mínimo</p>
                        <p className="font-medium">{policy.minScore}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">DTI Máximo</p>
                        <p className="font-medium">{formatPercent(policy.maxDebtToIncome || 0)}</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Idade Conta Mín.</p>
                        <p className="font-medium">{policy.minAccountAgeDays || 0} dias</p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">Auto-Aprovação</p>
                        <p className="font-medium">
                          {policy.autoApprovalThreshold
                            ? `≥ ${policy.autoApprovalThreshold}`
                            : 'Desabilitado'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex gap-2 pt-4 border-t">
                    <Button
                      variant="secondary"
                      onClick={() =>
                        (window.location.href = `/admin/credit/policies`)
                      }
                    >
                      Editar Política
                    </Button>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Informações Adicionais */}
      <Card>
        <CardHeader>
          <CardTitle>Sobre os Produtos de Crédito</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-2">👤 Empréstimo Pessoal</h4>
              <p className="text-sm text-gray-600">
                Crédito sem garantia para pessoas físicas. Ideal para despesas
                pessoais, consolidação de dívidas ou emergências. Taxas baseadas
                no perfil de risco do cliente.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">💼 Crédito Consignado</h4>
              <p className="text-sm text-gray-600">
                Parcelas descontadas diretamente da folha de pagamento. Taxas
                mais baixas devido à garantia do desconto em folha. Limite de
                35% do salário líquido.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">📊 Antecipação de Recebíveis</h4>
              <p className="text-sm text-gray-600">
                Antecipação de vendas a prazo (cartão, boleto). O valor é
                liberado imediatamente com desconto. Liquidação automática
                quando o recebível é pago.
              </p>
            </div>
            <div>
              <h4 className="font-medium mb-2">🏢 Capital de Giro</h4>
              <p className="text-sm text-gray-600">
                Crédito para empresas (PJ) financiarem suas operações. Análise
                baseada no histórico de transações e faturamento. Valores
                maiores e prazos mais longos.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default CreditProducts;
